<script setup lang="ts">
import {PropType, Ref, ref} from "vue";
import {IonButton, IonIcon} from "@ionic/vue";
import date from "@/helper/datetime/date";
import {
    AvailabilityCoordinate,
    BookingSuggestion,
    Day,
    DemandJoDaShTi,
    DemandJoDaShTiAmIAvailableFragment,
    DemandJoDaShTiFragment
} from "@/graphql/generated/graphql";
import Modal from "@/components/Modal.vue";
import {
    addAvailabilityCoordinateToDay,
    askChatForDemandOn,
    containsJoDaShTi,
    convertToDemandJoDaShTi,
    removeAvailabilityCoordinateFromDay
} from "@/helper/demandBrowser";
import {KapitelIcon} from "@/helper/icon";
import JoDaShTiListItem from "@/views/JoDaShTiListItem.vue";
import {claimBookingSuggestion, fetchBookingSuggestion} from "@/helper/bookingSuggestion";
import {useGlobalEmitter} from "@/helper/emitter";

const props = defineProps({
    day: {
        type: Object as PropType<Day>,
        required: true
    },
    additionalAvailabilitySummary: {
        type: Array as PropType<AvailabilityCoordinate[]> | [],
        required: false,
        default: [],
    },
    demandSuggestions: {
        type: Array as PropType<DemandJoDaShTi[]>,
        required: false,
        default: [],
    },
});

const isOpen = defineModel({type: Boolean, default: false})

// const emit = defineEmits<{
//     add: [d: DemandJoDaShTi]
//     remove: [d: DemandJoDaShTi]
// }>()

const localAdditionalAvailabilities : Ref<DemandJoDaShTiFragment[]> = ref([])
const localDemandSuggestions : Ref<(DemandJoDaShTiFragment & DemandJoDaShTiAmIAvailableFragment)[]> = ref([])
const demandSuggestionsByKey : Ref<Record<string, BookingSuggestion>> = ref({})

const demandKey = (d: {begin: string, until: string, jobsite: {id:number}}) =>
    `${d.jobsite.id}-${d.begin}-${d.until}`

/**
 * create local copy on modal-present, decoupled from outside changes
 * // prevent doubles by filtering suggestions to not match any existing additionalAvailability
 */
const init = async () => {
    localAdditionalAvailabilities.value = props.additionalAvailabilitySummary
        .map((aa) => convertToDemandJoDaShTi({...aa, instantBookable: false}))
    demandSuggestionsByKey.value = {}
    localDemandSuggestions.value = await Promise.all(
        props.demandSuggestions.map(async (d) => {
            const suggestion = await fetchBookingSuggestion(d.begin, d.until, d.jobsite.id)
            const instantBookable = !!suggestion
            if (suggestion) {
                demandSuggestionsByKey.value[demandKey(d)] = suggestion
            }
            return convertToDemandJoDaShTi({...d, instantBookable})
        })
    )
}

const isActive = (x:DemandJoDaShTiFragment) => props.day?.availability ? containsJoDaShTi(props.day?.availability.additionalAvailabilityCoordinates, x) : false

const toggle = async (x:DemandJoDaShTiFragment) => isActive(x)
    ? await removeAvailabilityCoordinateFromDay(x, props.day)
    : (
        x.instantBookable
            ? await claimBookingSuggestion(x.begin, x.until, x.jobsite.id).then(() => useGlobalEmitter().emit('day:mutated', props.day.date))
            : await addAvailabilityCoordinateToDay(x, props.day)
    )


</script>

<template>
    <Modal
        @view:willPresent="init"
        v-model="isOpen"
        title="Zusätzliche Verfügbarkeiten"
        :subtitle="'am ' + date.formatKapitelDate(day.date)"
    >
        <template v-if="localAdditionalAvailabilities.length > 0">
<!--            <h3>Zusätzlich Verfügbar für</h3>-->
            <JoDaShTiListItem
                v-for="aa in localAdditionalAvailabilities"
                :joDaShTi="aa"
                @toggle="toggle(aa)"
                :toggleState="isActive(aa)"
                :amIAvailable="isActive(aa) /* -> I think this is correct!  if we check for actual existing aa, the elements would be removed on toggle off*/"
                :showJobsiteLabel="true"
                :instant-bookable="aa.instantBookable"
            />
        </template>

        <h3 v-if="localAdditionalAvailabilities.length > 0 && localDemandSuggestions.length > 0" >Dienstvorschläge</h3>

        <template v-if="localDemandSuggestions.length > 0">
            <JoDaShTiListItem
                v-for="d in localDemandSuggestions"
                :joDaShTi="d"
                @toggle="toggle(d)"
                :toggleState="isActive(d)"
                :amIAvailable="d.amIAvailable ?? undefined"
                :showJobsiteLabel="true"
                :instant-bookable="d.instantBookable"
                :suggestion="demandSuggestionsByKey[demandKey(d)]"
            />
        </template>

<!--        <h3>Offene Dienste</h3>-->
        <IonButton class="footerButton" color="light" @click="askChatForDemandOn(day.date)">
            <IonIcon :icon="KapitelIcon.chat" slot="start"/>
            Weitere offene Dienste suchen
        </IonButton>

        <template #actions="{dismiss}">
            <IonButton color="primary" @click="dismiss">Zurück</IonButton>
        </template>
    </Modal>
</template>
<style scoped lang="scss">
    h3:not(:first-child),
    .footerButton{
        padding-top: 2em
    }
</style>
